import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsEnvelopeArrowUpFill } from "react-icons/bs";
import { FaPaperPlane, FaTrash } from "react-icons/fa";

import {
  getSentMessagesByUser,
  deleteMessageById,
} from "../../../services/messageServices";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

function IconButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-xs font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
      title={label}
    >
      <Icon />
      <span>{label}</span>
    </button>
  );
}

export default function MessagesSent({
  userId,
  refresh,
  setRefresh,
  onCounts,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const onCountsRef = useRef(onCounts);

  useEffect(() => {
    onCountsRef.current = onCounts;
  }, [onCounts]);

  const pushCounts = useCallback((list) => {
    onCountsRef.current?.({
      total: list.length,
    });
  }, []);

  const loadMessages = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const data = await getSentMessagesByUser(userId);
      const filtered = Array.isArray(data)
        ? data.filter((message) => message.is_copy)
        : [];

      setMessages(filtered);
      pushCounts(filtered);
    } catch (error) {
      console.error("Erreur lors du chargement des messages envoyés:", error);
      toast.error("Erreur lors du chargement des messages envoyés.");
    } finally {
      setLoading(false);
    }
  }, [pushCounts, userId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!refresh) return;

    setRefresh?.(false);
    loadMessages();
  }, [loadMessages, refresh, setRefresh]);

  const handleDeleteMessage = async (messageId) => {
    const confirmDelete = window.confirm("Supprimer ce message envoyé ?");

    if (!confirmDelete) return;

    try {
      await deleteMessageById(messageId);

      const next = messages.filter((message) => message.id !== messageId);

      setMessages(next);
      pushCounts(next);

      toast.success("Message supprimé avec succès.");
    } catch (error) {
      console.error("Erreur suppression message:", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-32 animate-pulse rounded-[1.5rem] bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
          <BsEnvelopeArrowUpFill />
        </div>

        <div>
          <p className="font-bold text-slate-950 dark:text-white">
            Messages envoyés
          </p>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            {messages.length} message{messages.length > 1 ? "s" : ""} envoyé
            {messages.length > 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="flex min-h-[260px] items-center justify-center rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950/50">
          <div>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-sky-600 shadow-md dark:bg-slate-900">
              <FaPaperPlane />
            </div>

            <h4 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
              Aucun message envoyé
            </h4>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Les messages envoyés apparaîtront ici.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => {
            const recipientName = `${message.Recipient?.nom || ""} ${
              message.Recipient?.prenom || ""
            }`.trim();

            return (
              <article
                key={message.id}
                className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/10"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                        <FaPaperPlane />
                      </span>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                          À {recipientName || "Destinataire inconnu"}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {fmtDate(message.created_at)} ·{" "}
                          {fmtTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <IconButton
                    icon={FaTrash}
                    label="Supprimer"
                    onClick={() => handleDeleteMessage(message.id)}
                  />
                </div>

                <h4 className="mt-3 text-base font-bold text-blue-700 dark:text-blue-300">
                  {message.subject}
                </h4>

                <div
                  className="prose prose-sm mt-2 max-w-none text-sm leading-6 text-slate-700 dark:prose-invert dark:text-slate-200"
                  dangerouslySetInnerHTML={{ __html: message.content }}
                />
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}